"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aggregateAirSensors = exports.selectAirSensors = void 0;
const types_1 = require("../../types");
const alertStatus_1 = require("../alertStatus");
const coverage_1 = require("../coverage");
const extractReadingValueFromSensorEvent_1 = require("../extractReadingValueFromSensorEvent");
const plausibility_1 = require("../plausibility");
const sensorClassification_1 = require("../sensorClassification");
const sensorInclusion_1 = require("../sensorInclusion");
/**
 * Air-category sensor filter, lifted out of the hook's `useMemo`.
 *
 * Air quality sensors only. The HVAC air handler used to be aggregated here
 * purely to surface filter health; that moved to useHVACAggregation, where the
 * static-pressure measurement it comes from actually belongs.
 *
 * No hubId filter — see `selectWaterSensors`: `sensors` is caller-scoped, and
 * on the hub display that scope includes linked gateways whose sensors carry
 * their own hubId.
 */
function selectAirSensors(sensors) {
    return sensors.filter((s) => (0, sensorInclusion_1.isDashboardSensor)(s) && s.type === types_1.SensorType.AIR_QUALITY);
}
exports.selectAirSensors = selectAirSensors;
/**
 * Pure air aggregation. Extracted verbatim from `useAirAggregation`'s `useMemo`
 * body. `airSensors` must already be `selectAirSensors`-filtered.
 */
function aggregateAirSensors(airSensors, fetchResults, loading, nowSeconds) {
    if (airSensors.length === 0) {
        return {
            sensors: [],
            sensorCount: 0,
            overallStatus: types_1.SensorCardStatus.Good,
            needsAttentionCount: 0,
            activeAlertCount: 0,
            offlineCount: 0,
            unknownCount: 0,
            coverage: 'full',
            averages: {
                indoorAQLabel: 'No Sensors',
                indoorAQStatus: types_1.SensorCardStatus.Good,
                avgTemperature: 0,
                avgTemperatureLabel: 'No Sensors',
                aqSensorCount: 0,
            },
            loading,
        };
    }
    const sensorSummaries = [];
    let overallStatus = types_1.SensorCardStatus.Good;
    let worstSensor;
    let needsAttentionCount = 0;
    let activeAlertCount = 0;
    let temperatureSum = 0;
    let temperatureCount = 0;
    let iaqSum = 0;
    let iaqCount = 0;
    let hasCalibratingSensor = false;
    let offlineAqCount = 0;
    const aqSensors = airSensors;
    // Process air quality sensors
    aqSensors.forEach((sensor) => {
        const result = fetchResults.get(sensor.id);
        const event = result?.event;
        const coverage = (0, coverage_1.sensorCoverageState)(sensor, nowSeconds);
        const isOffline = coverage === 'offline';
        let status = types_1.SensorCardStatus.Good;
        let value = 'Good';
        let needsAttention = false;
        let attentionMessage;
        // Offline wins over the last stored reading — stale IAQ must not
        // classify as "Good" or feed the average.
        if (isOffline) {
            offlineAqCount++;
            // Offline, not Error — a coverage gap, not a hazard. No needsAttention /
            // attentionMessage either: the coverage badge owns this row. See water.ts.
            status = types_1.SensorCardStatus.Offline;
            value = 'Offline';
        }
        else if (event) {
            // Try typed extraction, then direct values access, then value string parse
            let iaq = null;
            let calibrating = false;
            const reading = (0, extractReadingValueFromSensorEvent_1.extractReadingValueFromSensorEvent)(event);
            // Prefer rolling iaq (self-personalizes via BSEC's 28-day window —
            // produces honest "Moderate" labels for occupied rooms rather than
            // sIAQ's persistent overstatement). Fall back to staticIaq only if
            // iaq is missing on legacy payloads.
            const readingIaq = reading?.iaq ?? reading?.staticIaq;
            if (readingIaq !== undefined && readingIaq !== null) {
                iaq = readingIaq;
                calibrating = reading?.calibrating === 1;
            }
            else {
                const vals = event.values;
                if (vals?.iaq !== undefined) {
                    iaq = Number(vals.iaq);
                    calibrating = vals?.calibrating !== undefined && Number(vals.calibrating) === 1;
                }
                else if (event.value && typeof event.value === 'string') {
                    // AQ value format: temp,humidity,pressure,iaq,pm,battery[,calibrating]
                    const parts = event.value.split(',');
                    if (parts.length >= 4) {
                        const parsed = parseFloat(parts[3]);
                        if (!isNaN(parsed))
                            iaq = parsed;
                    }
                    if (parts.length >= 7) {
                        calibrating = parseFloat(parts[6]) === 1;
                    }
                }
            }
            // Indoor temperature, °C → °F. `compTemperature` FIRST: the raw BME688
            // temp includes the sensor's own heater self-heating, which is what
            // produced a field unit reading 137°F in a ~26°C room. -1566 is the
            // firmware's fault sentinel and must never reach an average.
            const rawTempC = reading?.compTemperature ?? reading?.temperature;
            if (rawTempC !== undefined && rawTempC !== null && rawTempC !== -1566 && !isNaN(rawTempC)) {
                // -1566 is the known sentinel; the range guard catches the rest (a
                // 260°C indoor reading is not a hot room, it is a broken sensor).
                const tempF = (rawTempC * 9) / 5 + 32;
                if ((0, plausibility_1.isPlausible)('temperatureF', tempF)) {
                    temperatureSum += tempF;
                    temperatureCount++;
                }
            }
            // BSEC-derived metrics beyond IAQ — read directly off the typed
            // reading (same underlying object as event.values). Undefined on
            // legacy pre-BSEC payloads; the classifiers below treat that as Good.
            const co2Equivalent = reading?.co2Equivalent;
            const breathVoc = reading?.breathVoc;
            // A PM reading outside the plausible range is a sensor fault, not dirty
            // air — a field unit reported 176144178222 mg/m3 and painted the card
            // red. Drop it from the status fold entirely (below) rather than letting
            // pmToStatus grade it. A non-numeric/absent `pm` stays "missing" as
            // before; only a genuine number can be judged implausible.
            const pmRaw = reading?.pm;
            const pmImplausible = typeof pmRaw === 'number' && !(0, plausibility_1.isPlausible)('pm25', pmRaw);
            const pm = pmImplausible ? undefined : pmRaw;
            if (calibrating) {
                // Sensor reboot detected — show calibrating instead of potentially
                // alarming readings. Skip this reading entirely (all metrics).
                status = types_1.SensorCardStatus.Calibrating;
                value = 'Calibrating...';
                hasCalibratingSensor = true;
            }
            else {
                const metricResults = [];
                if (iaq !== null && !isNaN(iaq)) {
                    // BSEC IAQ range is 0-500; anything above is raw gas_resistance
                    // that leaked through before BSEC processed the line. Clamp it.
                    iaq = Math.min(iaq, 500);
                    iaqSum += iaq;
                    iaqCount++;
                    value = `${(0, sensorClassification_1.iaqToLabel)(iaq)} (${Math.round(iaq)})`;
                    metricResults.push({ status: (0, sensorClassification_1.iaqToStatus)(iaq), message: `IAQ ${Math.round(iaq)}` });
                }
                if (co2Equivalent !== undefined && !isNaN(co2Equivalent)) {
                    metricResults.push({
                        status: (0, sensorClassification_1.co2ToStatus)(co2Equivalent),
                        message: `CO₂ ${Math.round(co2Equivalent)}`,
                    });
                }
                if (breathVoc !== undefined && !isNaN(breathVoc)) {
                    metricResults.push({
                        status: (0, sensorClassification_1.vocToStatus)(breathVoc),
                        message: `VOC ${Math.round(breathVoc)}`,
                    });
                }
                if (pm !== undefined && !isNaN(pm)) {
                    metricResults.push({ status: (0, sensorClassification_1.pmToStatus)(pm), message: `PM2.5 ${pm.toFixed(2)}` });
                }
                status = metricResults.reduce((worst, metric) => (0, sensorClassification_1.worstStatus)(worst, metric.status), types_1.SensorCardStatus.Good);
                if (status !== types_1.SensorCardStatus.Good) {
                    needsAttention = true;
                    const worstMetric = metricResults.find((metric) => metric.status === status);
                    attentionMessage = `${sensor.location || sensor.name}: ${worstMetric?.message}`;
                }
                // An invalid reading is amber, never red: nothing in the home is
                // wrong, the sensor is. worstStatus so a real hazard on another
                // channel still outranks it — but a row carrying garbage can never
                // read green either.
                if (pmImplausible) {
                    status = (0, sensorClassification_1.worstStatus)(status, types_1.SensorCardStatus.Poor);
                    needsAttention = true;
                    attentionMessage = `${sensor.location || sensor.name}: ${plausibility_1.INVALID_READING_LABEL}`;
                }
            }
        }
        else {
            // Paired but has never reported — same initializer leak the water core
            // had: without this the Good/'Good' defaults above render a sensor that
            // has never sent a packet as healthy.
            //
            // Unknown, not Calibrating. Calibrating is reserved for the real BSEC
            // warm-up transient handled above (sequence-number reset), which is a
            // sensor that IS reporting and whose numbers are briefly untrustworthy —
            // a different thing from one that has never reported at all. Unknown
            // ranks 1: visible, but unable to drag the category card down or
            // suppress a sibling sensor's readings.
            status = types_1.SensorCardStatus.Unknown;
            value = 'Awaiting data';
        }
        // Alerts can only worsen the reading-derived verdict — a sensor with an
        // open alert must never render green. STATUS folds every active alert
        // (type-aware — the offline row lands on Offline); the HAZARD channel
        // (counts + badge copy) excludes the offline row. See water.ts.
        const sensorAlerts = result?.alerts ?? [];
        const sensorHazards = (0, alertStatus_1.hazardAlerts)(sensorAlerts);
        activeAlertCount += sensorHazards.length;
        if (sensorAlerts.length > 0) {
            status = (0, sensorClassification_1.worstStatus)(status, (0, alertStatus_1.activeAlertsToStatus)(sensorAlerts));
        }
        if (sensorHazards.length > 0) {
            needsAttention = true;
            attentionMessage = (0, alertStatus_1.alertAttentionMessage)(sensor.location || sensor.name || 'Air Quality', sensorHazards);
        }
        const summary = {
            sensorId: sensor.id,
            name: sensor.name || sensor.location || 'Air Quality',
            location: sensor.location || '',
            typeLabel: 'Air Quality Sensor',
            sensorType: sensor.type,
            status,
            value,
            needsAttention,
            attentionMessage,
            isOffline,
            coverage,
            lastReportedAt: sensor.lastReported,
            alertCount: sensorHazards.length,
        };
        sensorSummaries.push(summary);
        if (needsAttention)
            needsAttentionCount++;
        const newOverall = (0, sensorClassification_1.worstStatus)(overallStatus, status);
        if (newOverall !== overallStatus) {
            overallStatus = newOverall;
            worstSensor = summary;
        }
    });
    // Compute averages
    const avgTemperature = temperatureCount > 0 ? Math.round(temperatureSum / temperatureCount) : 0;
    const allAqOfflineForTemp = aqSensors.length > 0 && offlineAqCount === aqSensors.length;
    const avgTemperatureLabel = temperatureCount > 0
        ? `${avgTemperature}°F`
        : allAqOfflineForTemp
            ? 'Not monitored'
            : aqSensors.length === 0
                ? 'No Sensors'
                : 'No Data';
    const avgIaq = iaqCount > 0 ? Math.round(iaqSum / iaqCount) : 0;
    const allCalibrating = iaqCount === 0 && aqSensors.length > 0 && hasCalibratingSensor;
    const allAqOffline = aqSensors.length > 0 && offlineAqCount === aqSensors.length;
    const indoorAQLabel = iaqCount > 0
        ? `${(0, sensorClassification_1.iaqToLabel)(avgIaq)} (${avgIaq})`
        : allAqOffline
            ? 'Not monitored'
            : allCalibrating
                ? 'Calibrating...'
                : aqSensors.length === 0
                    ? 'No Sensors'
                    : 'No Data';
    const indoorAQStatus = iaqCount > 0
        ? (0, sensorClassification_1.iaqToStatus)(avgIaq)
        : allAqOffline
            // Offline, not Error: no air quality is being measured, which is a gap
            // in what we can see rather than bad air.
            ? types_1.SensorCardStatus.Offline
            : allCalibrating ? types_1.SensorCardStatus.Calibrating : types_1.SensorCardStatus.Good;
    // Rolled up from the per-row coverage states — one source of truth across
    // all four categories, so the never-paired split can't drift from the rows.
    const rollup = (0, coverage_1.categoryCoverage)(sensorSummaries);
    return {
        sensors: sensorSummaries,
        sensorCount: airSensors.length,
        overallStatus,
        needsAttentionCount,
        activeAlertCount,
        offlineCount: rollup.offlineCount,
        unknownCount: rollup.unknownCount,
        coverage: rollup.coverage,
        worstSensor,
        averages: {
            indoorAQLabel,
            indoorAQStatus,
            avgTemperature,
            avgTemperatureLabel,
            aqSensorCount: aqSensors.length,
        },
        loading,
    };
}
exports.aggregateAirSensors = aggregateAirSensors;
